import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, EnvelopeSimple, CheckCircle, Clock, Globe, PaperPlaneTilt, SlackLogo, X } from "@phosphor-icons/react";

// Curated short list of common business timezones — covers ~95% of supervisors.
const TZ_OPTIONS = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

function detectTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (e) { return "UTC"; }
}

export default function Settings() {
  const [prefs, setPrefs] = useState({
    daily_email: false,
    timezone: detectTz(),
    delivery_hour: 7,
    slack_webhook_url: "",
    slack_alerts_enabled: false,
    slack_alert_level: "critical",
    slack_routing: [],
  });
  const [slackUrlDraft, setSlackUrlDraft] = useState("");
  const [routeDraft, setRouteDraft] = useState({ industry: "", webhook_url: "" });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSentDate, setLastSentDate] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingSlackTest, setSendingSlackTest] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/notifications");
        const next = {
          daily_email: !!data.daily_email,
          timezone: TZ_OPTIONS.includes(data.timezone) ? data.timezone : detectTz(),
          delivery_hour: typeof data.delivery_hour === "number" ? data.delivery_hour : 7,
          slack_webhook_url: data.slack_webhook_url || "",
          slack_alerts_enabled: !!data.slack_alerts_enabled,
          slack_alert_level: data.slack_alert_level || "critical",
          slack_routing: Array.isArray(data.slack_routing) ? data.slack_routing : [],
        };
        setPrefs(next);
        setSlackUrlDraft(next.slack_webhook_url);
        setLastSentDate(data.last_daily_email_date || null);
      } catch (e) { toast.error("Could not load preferences"); }
      finally { setLoaded(true); }
    })();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/notifications", next);
      const merged = {
        daily_email: !!data.daily_email,
        timezone: data.timezone,
        delivery_hour: data.delivery_hour,
        slack_webhook_url: data.slack_webhook_url || "",
        slack_alerts_enabled: !!data.slack_alerts_enabled,
        slack_alert_level: data.slack_alert_level || "critical",
        slack_routing: Array.isArray(data.slack_routing) ? data.slack_routing : [],
      };
      setPrefs(merged);
      setSlackUrlDraft(merged.slack_webhook_url);
      toast.success("Saved");
      return merged;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      throw e;
    } finally { setSaving(false); }
  };

  const toggleEmail = (val) => save({ ...prefs, daily_email: !!val });
  const setTz = (v) => save({ ...prefs, timezone: v });
  const setHour = (v) => save({ ...prefs, delivery_hour: parseInt(v, 10) });

  const sendTestNow = async () => {
    setSendingTest(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await api.post("/report/email", { start: today, end: today });
      toast.success(`Test sent to ${data.to} (${data.count} clip${data.count === 1 ? "" : "s"})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send test"); }
    finally { setSendingTest(false); }
  };

  const saveSlackUrl = async () => {
    try { await save({ ...prefs, slack_webhook_url: slackUrlDraft.trim() }); }
    catch (e) { /* save already toasted */ }
  };

  const sendSlackTest = async () => {
    setSendingSlackTest(true);
    try {
      const current = (prefs.slack_webhook_url || "").trim();
      const draft = (slackUrlDraft || "").trim();
      if (draft && draft !== current) {
        await save({ ...prefs, slack_webhook_url: draft });
      }
      await api.post("/me/notifications/slack-test");
      toast.success("Slack test posted — check your channel");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post to Slack"); }
    finally { setSendingSlackTest(false); }
  };

  const toggleSlackAlerts = (val) => save({ ...prefs, slack_alerts_enabled: !!val });
  const setSlackLevel = (v) => save({ ...prefs, slack_alert_level: v });

  const addRoute = async () => {
    const industry = (routeDraft.industry || "").trim();
    const webhook = (routeDraft.webhook_url || "").trim();
    if (!industry) { toast.error("Pick an industry"); return; }
    if (!webhook.startsWith("https://hooks.slack.com/")) { toast.error("Webhook must start with https://hooks.slack.com/"); return; }
    const next = [...(prefs.slack_routing || []).filter((r) => r.industry !== industry), { industry, webhook_url: webhook }];
    try {
      await save({ ...prefs, slack_routing: next });
      setRouteDraft({ industry: "", webhook_url: "" });
    } catch (e) { /* toasted */ }
  };

  const removeRoute = async (industry) => {
    const next = (prefs.slack_routing || []).filter((r) => r.industry !== industry);
    try { await save({ ...prefs, slack_routing: next }); }
    catch (e) { /* toasted */ }
  };

  const INDUSTRIES = ["Manufacturing","Warehouse & Logistics","Construction","Healthcare","Retail & Hospitality","Agriculture","Office / Computer Work","Other"];

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors position="top-right" />
      <div className="max-w-[920px] mx-auto px-6 lg:px-10 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-900" data-testid="settings-back">
          <ArrowLeft size={14} weight="bold" /> Back to dashboard
        </Link>
        <div className="mt-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">Settings</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink-900">Notifications</h1>
          <p className="mt-3 text-ink-700 max-w-2xl">Two destinations: a designed HTML email digest and Slack pings for the most dangerous moments. Both opt-in. Neither is noisy by default.</p>
        </div>

        {/* Daily email */}
        <div className="mt-10 border-2 border-ink-900" data-testid="daily-email-card">
          <div className="p-6 border-b border-ink-100 flex items-start gap-4">
            <div className="w-10 h-10 bg-ink-900 flex items-center justify-center shrink-0">
              <EnvelopeSimple size={18} weight="bold" className="text-white" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-xl tracking-tight text-ink-900">Daily roll-up email</div>
              <div className="mt-1 text-sm text-ink-700">A designed HTML digest of yesterday's completed clips — risk scores, top hazards, deep-links.</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">{prefs.daily_email ? "On" : "Off"}</span>
              <Switch
                data-testid="daily-email-toggle"
                checked={prefs.daily_email}
                onCheckedChange={toggleEmail}
                disabled={!loaded || saving}
              />
            </div>
          </div>

          <div className={`p-6 grid grid-cols-1 md:grid-cols-2 gap-6 ${prefs.daily_email ? "" : "opacity-50 pointer-events-none"}`} data-testid="daily-email-schedule">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 flex items-center gap-1.5"><Globe size={12} weight="bold" /> Timezone</Label>
              <Select value={prefs.timezone} onValueChange={setTz}>
                <SelectTrigger data-testid="daily-email-tz" className="mt-2 rounded-none border-ink-100"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{TZ_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.25em] text-ink-500">Auto-detected: {detectTz()}</div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 flex items-center gap-1.5"><Clock size={12} weight="bold" /> Delivery hour</Label>
              <Select value={String(prefs.delivery_hour)} onValueChange={setHour}>
                <SelectTrigger data-testid="daily-email-hour" className="mt-2 rounded-none border-ink-100"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {Array.from({ length: 24 }).map((_, h) => {
                    const ampm = h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`;
                    return <SelectItem key={h} value={String(h)}>{ampm} ({String(h).padStart(2, "0")}:00)</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.25em] text-ink-500">Local time · in {prefs.timezone}</div>
            </div>
          </div>

          <div className="p-6 border-t border-ink-100 bg-ink-50 flex flex-wrap items-center justify-between gap-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-700">
              {lastSentDate ? <>Last delivery · {lastSentDate}</> : <>No daily digest sent yet</>}
            </div>
            <Button
              data-testid="daily-email-test"
              onClick={sendTestNow}
              disabled={sendingTest}
              variant="outline"
              size="sm"
              className="rounded-none border-ink-100 hover:border-ink-900"
            >
              <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
              {sendingTest ? "Sending…" : "Send a test now"}
            </Button>
          </div>
        </div>

        <div className="mt-8 border border-ink-100 p-5 bg-ink-50 text-sm text-ink-700 flex items-start gap-3">
          <CheckCircle size={18} weight="bold" className="text-safety mt-0.5 shrink-0" />
          <div>
            <strong className="text-ink-900">Empty days are skipped automatically.</strong> If no analyses completed in the last 24 hours at your delivery hour, no email is sent — so your inbox doesn't get noise.
          </div>
        </div>

        {/* Slack alerts */}
        <div className="mt-10 border-2 border-ink-900" data-testid="slack-card">
          <div className="p-6 border-b border-ink-100 flex items-start gap-4">
            <div className="w-10 h-10 bg-ink-900 flex items-center justify-center shrink-0">
              <SlackLogo size={18} weight="bold" className="text-white" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-xl tracking-tight text-ink-900">Slack alerts</div>
              <div className="mt-1 text-sm text-ink-700">Post a compact message to a Slack channel when a critical workplace hazard is detected.</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">{prefs.slack_alerts_enabled ? "On" : "Off"}</span>
              <Switch
                data-testid="slack-toggle"
                checked={prefs.slack_alerts_enabled}
                onCheckedChange={toggleSlackAlerts}
                disabled={!loaded || saving || !prefs.slack_webhook_url}
              />
            </div>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Incoming-webhook URL</Label>
              <div className="mt-2 flex flex-col sm:flex-row gap-2">
                <input
                  type="url"
                  data-testid="slack-webhook-input"
                  value={slackUrlDraft}
                  onChange={(e) => setSlackUrlDraft(e.target.value)}
                  placeholder="https://hooks.slack.com/services/T0/B0/xxxxxxxx"
                  className="flex-1 min-w-0 px-3 h-10 border border-ink-100 rounded-none focus:outline-none focus:border-ink-900 font-mono text-xs"
                />
                <Button
                  data-testid="slack-save-url"
                  onClick={saveSlackUrl}
                  disabled={saving || (slackUrlDraft || "").trim() === (prefs.slack_webhook_url || "").trim()}
                  variant="outline"
                  className="rounded-none border-ink-100 hover:border-ink-900"
                >
                  Save URL
                </Button>
              </div>
              <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.25em] text-ink-500">
                Create at Slack → Apps → Incoming Webhooks. Must start with <span className="text-ink-900">https://hooks.slack.com/</span>
              </div>
            </div>

            <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${prefs.slack_webhook_url ? "" : "opacity-50 pointer-events-none"}`} data-testid="slack-schedule">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Alert threshold</Label>
                <Select value={prefs.slack_alert_level} onValueChange={setSlackLevel}>
                  <SelectTrigger data-testid="slack-level" className="mt-2 rounded-none border-ink-100"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical only</SelectItem>
                    <SelectItem value="high_or_critical">High or critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  data-testid="slack-test"
                  onClick={sendSlackTest}
                  disabled={sendingSlackTest || !((slackUrlDraft || prefs.slack_webhook_url || "").trim())}
                  variant="outline"
                  className="rounded-none border-ink-100 hover:border-ink-900 w-full h-10"
                >
                  <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
                  {sendingSlackTest ? "Posting…" : "Send a test to Slack"}
                </Button>
              </div>
            </div>

            {/* Per-industry routing rules */}
            <div className="pt-4 border-t border-ink-100" data-testid="slack-routing">
              <div className="flex items-baseline justify-between mb-3">
                <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Route by industry (optional)</Label>
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-ink-500">Falls back to default URL when no rule matches</span>
              </div>
              {(prefs.slack_routing || []).length > 0 && (
                <div className="border border-ink-100 divide-y divide-ink-100 mb-3">
                  {prefs.slack_routing.map((r) => (
                    <div key={r.industry} className="p-3 grid grid-cols-12 gap-2 items-center" data-testid={`route-${r.industry}`}>
                      <div className="col-span-3 font-display font-semibold text-ink-900 text-sm truncate">{r.industry}</div>
                      <div className="col-span-8 font-mono text-[11px] text-ink-700 truncate" title={r.webhook_url}>{r.webhook_url}</div>
                      <div className="col-span-1 text-right">
                        <button onClick={() => removeRoute(r.industry)} className="text-ink-500 hover:text-hazard-critical" aria-label="Remove rule" data-testid={`route-remove-${r.industry}`}>
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-12 md:col-span-3">
                  <Select value={routeDraft.industry || "__none__"} onValueChange={(v) => setRouteDraft({ ...routeDraft, industry: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="route-industry" className="rounded-none border-ink-100 h-10"><SelectValue placeholder="Industry" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Industry…</SelectItem>
                      {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-12 md:col-span-7">
                  <input
                    type="url"
                    data-testid="route-webhook"
                    value={routeDraft.webhook_url}
                    onChange={(e) => setRouteDraft({ ...routeDraft, webhook_url: e.target.value })}
                    placeholder="https://hooks.slack.com/services/T0/B0/xxxxxxxx"
                    className="w-full px-3 h-10 border border-ink-100 rounded-none focus:outline-none focus:border-ink-900 font-mono text-xs"
                  />
                </div>
                <div className="col-span-12 md:col-span-2">
                  <Button onClick={addRoute} data-testid="route-add" className="w-full rounded-none bg-ink-900 hover:bg-safety text-white h-10">
                    Add rule
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
